import { LanguageFlag } from 'components/facets/LanguageFlag'
import {
  CountryEdition,
  countryEditionMap,
  FINISH_TYPES_CONVERSIONS,
  FINISH_TYPE_LABELS,
  flagCode,
} from 'shared'
import {
  COLD_FOIL_ICON,
  COSMOS_HOLO_ICON,
  FIRST_EDITION_ICON,
  FOIL_ICON,
  ICE_CRACKED_HOLO_ICON,
  MASTERBALL_HOLO_ICON,
  POKEBALL_HOLO_ICON,
  RAINBOW_FOIL_ICON,
  REVERSE_COSMOS_ICON,
  REVERSE_HOLO_ICON,
  REVERSE_ICE_CRACKED_ICON,
  REVERSE_MASTERBALL_ICON,
  REVERSE_POKEBALL_ICON,
  REVERSE_STAMPED_ICON,
  SIGNED_ICON,
  STAMPED_HOLO_ICON,
} from 'utils/constants'
import { ConditionBadge } from './ConditionBadge'
import { CustomTooltip } from './CustomTooltip'

interface ArticleAttributesProps {
  article: any
  iconHeight: number
  onlyExtraAttributes?: boolean
  identifierForDataTestId?: string
}

const FINISH_TYPE_ICON_MAP: Record<
  string,
  { alt: string; src: string; testIdSuffix?: string; className?: string }
> = {
  [FINISH_TYPE_LABELS.FOIL]: {
    alt: 'Foil',
    src: FOIL_ICON,
    testIdSuffix: 'foil',
    className: 'me-2',
  },
  [FINISH_TYPE_LABELS.RAINBOW_FOIL]: {
    alt: 'Rainbow Foil',
    src: RAINBOW_FOIL_ICON,
    testIdSuffix: 'rainbowFoil',
    className: 'me-2',
  },
  [FINISH_TYPE_LABELS.COLD_FOIL]: {
    alt: 'Cold Foil',
    src: COLD_FOIL_ICON,
    testIdSuffix: 'coldFoil',
    className: 'me-2',
  },
  [FINISH_TYPE_LABELS.REVERSE_HOLO]: {
    alt: 'Reverse Holo',
    src: REVERSE_HOLO_ICON,
    testIdSuffix: 'reverseHolo',
    className: 'me-2',
  },
  [FINISH_TYPE_LABELS.STAMPED_HOLO]: { alt: 'Stamped Holo', src: STAMPED_HOLO_ICON },
  [FINISH_TYPE_LABELS.REVERSE_STAMPED_HOLO]: {
    alt: 'Reverse Stamped Holo',
    src: REVERSE_STAMPED_ICON,
  },
  [FINISH_TYPE_LABELS.COSMOS_HOLO]: { alt: 'Cosmos Holo', src: COSMOS_HOLO_ICON },
  [FINISH_TYPE_LABELS.REVERSE_COSMOS_HOLO]: {
    alt: 'Reverse Cosmos Holo',
    src: REVERSE_COSMOS_ICON,
  },
  [FINISH_TYPE_LABELS.ICE_CRACKED_HOLO]: { alt: 'Ice Cracked Holo', src: ICE_CRACKED_HOLO_ICON },
  [FINISH_TYPE_LABELS.REVERSE_ICE_CRACKED_HOLO]: {
    alt: 'Reverse Ice Cracked Holo',
    src: REVERSE_ICE_CRACKED_ICON,
  },
  [FINISH_TYPE_LABELS.MASTERBALL_HOLO]: { alt: 'Masterball Holo', src: MASTERBALL_HOLO_ICON },
  [FINISH_TYPE_LABELS.POKEBALL_HOLO]: { alt: 'Pokeball Holo', src: POKEBALL_HOLO_ICON },
  [FINISH_TYPE_LABELS.REVERSE_MASTERBALL_HOLO]: {
    alt: 'Reverse Masterball Holo',
    src: REVERSE_MASTERBALL_ICON,
  },
  [FINISH_TYPE_LABELS.REVERSE_POKEBALL_HOLO]: {
    alt: 'Reverse Pokeball Holo',
    src: REVERSE_POKEBALL_ICON,
  },
}

export const ArticleAttributes = ({
  article,
  iconHeight,
  onlyExtraAttributes = false,
  identifierForDataTestId,
}: ArticleAttributesProps): JSX.Element => {
  let finishType
  if (typeof article.finishType === 'number')
    finishType = FINISH_TYPES_CONVERSIONS.find((entry) => entry.code === article.finishType)!.label
  else finishType = article.finishType

  let countryEdition = article.countryEdition
  if (typeof countryEdition === 'number')
    countryEdition = countryEditionMap.find((entry) => entry.code === countryEdition)?.value

  const finishIcon = FINISH_TYPE_ICON_MAP[finishType]

  const finishTypeIcon = finishIcon && (
    <div
      data-testid={
        finishIcon.testIdSuffix
          ? `${identifierForDataTestId ?? ''}${finishIcon.testIdSuffix}`
          : finishType
      }
      className={finishIcon.className}
    >
      <img alt={finishIcon.alt} height={iconHeight} src={finishIcon.src} />
    </div>
  )

  const extraAttributes = (
    <>
      {finishTypeIcon}

      {article.isSigned && (
        <div data-testid={`${identifierForDataTestId}-signed`} className="me-2">
          <img alt="Signed" height={iconHeight} src={SIGNED_ICON} />
        </div>
      )}

      {article.isFirstEd && (
        <div data-testid={`${identifierForDataTestId}-firstEd`} className="me-2">
          <img alt="First Edition" height={iconHeight} src={FIRST_EDITION_ICON} />
        </div>
      )}

      {countryEdition === CountryEdition.US && (
        <CustomTooltip title="US Edition">
          <LanguageFlag
            data-testid={`${identifierForDataTestId}US`}
            height={iconHeight}
            code={'us'}
          />
        </CustomTooltip>
      )}

      {article.gradingCompany && (
        <CustomTooltip title="Graded card">
          <>
            <i style={{ fontSize: '17px' }} className="fa-solid fa-fire grading-icon me-1"></i>
            <span
              data-testid={article.ptProductId + '-' + article.gradingCompany + '-' + article.grade}
            >
              {article.gradingCompany} - {article.grade ?? 'Unknown'}
            </span>
          </>
        </CustomTooltip>
      )}
    </>
  )

  if (onlyExtraAttributes)
    return (
      <div className="d-flex flex-wrap align-items-center justify-content-start gap-2">
        {extraAttributes}
      </div>
    )

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-start">
      <LanguageFlag
        height={iconHeight}
        code={flagCode(article.idLanguage ?? article.language.idLanguage) || 'gg'}
        style={{ alignSelf: 'center' }}
      />
      <div className="mx-2">
        <ConditionBadge condition={article.condition} />
      </div>
      {extraAttributes}
    </div>
  )
}
